// src/pages/memberManagement/memberDetails/memberDetails.js
import React, { useCallback, useEffect, useMemo, useReducer, useState } from "react";
import { useParams } from "react-router-dom";
import dayjs from "dayjs";
import { Button, Checkbox, DatePicker, Form, Input, Modal, Spin } from "antd";
import axios from "../../../services/axiosConfig";
import API_ENDPOINTS from "../../../services/apiEndpoints";
import { getAuthToken } from "../../../auth/auth";
import useNotification from "../../../hooks/useNotification";
import useFileViewer from "../../../hooks/useFileViewer";
import Layout from "../../../components/layout/layout";
import MemberUpdateHistory from "../components/MemberUpdateHistory";
import "./memberDetails.css";

const { TextArea } = Input;

// Initial state for useReducer
const initialState = {
  isLoading: true,
  showModal: false,
  messageBody: "",
  isSendingMessage: false,
  formData: {
    fullName: "",
    phoneNumber: "",
    email: "",
    password: "",
    dateOfBirth: "",
    maritalStatus: "",
    familyMembers: "",
    roleType: "",
    baptismStatus: "",
    address: "",
    testimony: "",
    additionalField1: "",
    additionalField2: "",
    areasOfInterest: "",
    baptismCertificateUrl: "",
    countryCode: "",
    customInterest: "",
    dateOfBaptism: "",
    documentURL: "",
    familyMemberNumber: "",
    familyMemberNames: "",
    fileName: "",
    marriageCertificateUrl: "",
    mimeType: "",
    residenceProofUrl: "",
    userId: "",
    status: "",
    termsAccepted: false,
    profileImage: null,
    residenceProof: "",
    marriageCertificate: "",
    baptismCertificate: ""
  },
  notification: {
    show: false,
    message: "",
    type: "success"
  }
};

// Reducer for state management
const memberReducer = (state, action) => {
  switch (action.type) {
    case "setLoading":
      return { ...state, isLoading: action.payload };
    case "setFormData":
      return { ...state, formData: action.payload };
    case "setShowModal":
      return { ...state, showModal: action.payload };
    case "setMessageBody":
      return { ...state, messageBody: action.payload };
    case "setIsSendingMessage":
      return { ...state, isSendingMessage: action.payload };
    case "setNotification":
      return { ...state, notification: action.payload };
    case "resetMessageModal":
      return { ...state, showModal: false, messageBody: "" };
    default:
      return state;
  }
};

function MemberDetails() {
  const { notification, showNotification, hideNotification } = useNotification();
  const [form] = Form.useForm();
  const [state, dispatch] = useReducer(memberReducer, initialState);
  const { membershipId } = useParams();
  const [updateLogs, setUpdateLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [logsError, setLogsError] = useState(null);
  const [markAsReadLoadingId, setMarkAsReadLoadingId] = useState(null);
  const { isLoading, showModal, messageBody, isSendingMessage, formData } = state;

  const { handleViewFile: hookHandleViewFile, loadingFiles } = useFileViewer(formData.userId);

  const viewSupportingDoc = useCallback(
    async (fileUrl, fileKey) => {
      if (!fileUrl) return;

      // Backend may need full S3 key or just filename — attempt both.
      let fullKey = fileUrl;
      let fileNameOnly = fileUrl;

      try {
        const parsed = new URL(String(fileUrl));
        const pathname = parsed.pathname.replace(/^\//, "");
        fullKey = pathname || decodeURIComponent(parsed.pathname.split("/").pop() || "");
        fileNameOnly = decodeURIComponent(parsed.pathname.split("/").pop() || "");
      } catch {
        const parts = String(fileUrl).split("/");
        fileNameOnly = parts[parts.length - 1] || fileUrl;
        fullKey = parts.length > 1 ? fileUrl : fileNameOnly;
      }

      let err = await hookHandleViewFile(fullKey, fileKey, "membership");
      if (err && fullKey !== fileNameOnly) {
        err = await hookHandleViewFile(fileNameOnly, fileKey, "membership");
      }

      if (err) {
        if (typeof fileUrl === "string" && fileUrl.startsWith("http")) {
          window.open(fileUrl, "_blank", "noopener,noreferrer");
          return;
        }

        showNotification(err, "error", "File Viewer");
      }
    },
    [hookHandleViewFile, showNotification]
  );

  // Handle send message
  const handleSendMessage = useCallback(async () => {
    if (!messageBody.trim()) {
      showNotification("Please enter a message", "error");
      return;
    }

    dispatch({ type: "setIsSendingMessage", payload: true });

    try {
      const response = await axios.post(API_ENDPOINTS.SEND_NOTIFICATIONS, {
        userId: formData.userId,
        title: "Message related to your Membership Form",
        body: messageBody,
        type: "membership"
      });

      console.log("send message to userId:", formData.userId);

      if (response.status === 200) {
        showNotification("Message sent successfully!", "success");
        dispatch({ type: "resetMessageModal" });
      } else {
        showNotification("Failed to send message: " + (response.data?.message || "Unknown error"), "error");
      }
    } catch (error) {
      console.error("Error sending message:", error);
      showNotification(error.response?.data?.message || "An error occurred while sending the message.", "error");
    } finally {
      dispatch({ type: "setIsSendingMessage", payload: false });
    }
  }, [messageBody, formData.userId, showNotification]);

  // Handle modal cancel
  const handleModalCancel = useCallback(() => {
    dispatch({ type: "resetMessageModal" });
  }, []);

  // Handle modal open
  const handleOpenModal = useCallback(() => {
    dispatch({ type: "setShowModal", payload: true });
  }, []);

  // Handle message body change
  const handleMessageBodyChange = useCallback((e) => {
    dispatch({ type: "setMessageBody", payload: e.target.value });
  }, []);

  // Handle cancel/back navigation
  const handleCancel = useCallback(() => {
    window.history.back();
  }, []);

  /**
   * IMPROVED: Handle marking a notification as read
   * Now uses adminNotificationId if available, falls back to notificationId,
   * and has better error handling
   */
  const handleMarkAsRead = useCallback(
    async (update) => {
      if (!update?.isUnread) {
        console.log("[MemberDetails] markAsRead: item already read, skipping", update);
        return;
      }

      const updateKey = update.eventId || update.notificationId || update.adminNotificationId;
      setMarkAsReadLoadingId(updateKey);

      console.log("[MemberDetails] markAsRead: start", {
        notificationId: update.notificationId,
        adminNotificationId: update.adminNotificationId,
        eventId: update.eventId,
        isUnread: update.isUnread
      });

      // Show loading state on the button (optional - you might want to add this to UI)
      // For now, we'll just log and proceed

      try {
        const token = await getAuthToken();

        // Use adminNotificationId if available (from the updated Lambda), otherwise fallback
        const notificationIdToUse = update.adminNotificationId || update.notificationId;

        if (!notificationIdToUse) {
          console.error("[MemberDetails] No notification ID available for update:", update);

          // Try to find by eventId as last resort
          console.log("[MemberDetails] Attempting to find notification by eventId:", update.eventId);

          // Fetch admin notifications to find by eventId
          const adminNotifResponse = await axios.get(API_ENDPOINTS.ADMIN_NOTIFICATIONS, {
            headers: { Authorization: `Bearer ${token}` }
          });

          const adminNotifications = adminNotifResponse.data.items || [];
          const matchingAdminNotif = adminNotifications.find((notif) => notif.eventId === update.eventId);

          if (!matchingAdminNotif) {
            setMarkAsReadLoadingId(null);
            showNotification("Could not find notification to mark as read", "error");
            return;
          }

          // Use the found notification ID
          const foundNotificationId = matchingAdminNotif.notificationId;
          console.log("[MemberDetails] Found notification by eventId:", {
            eventId: update.eventId,
            foundNotificationId
          });

          // Call API with found ID
          const markAsReadUrl = `${API_ENDPOINTS.ADMIN_NOTIFICATIONS}/${foundNotificationId}/read`;
          console.log("[MemberDetails] markAsRead: calling API with found ID", {
            url: markAsReadUrl
          });

          const response = await axios.put(markAsReadUrl, null, {
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json"
            }
          });

          console.log("[MemberDetails] markAsRead: API success", {
            status: response.status,
            data: response.data
          });

          // Update local state - mark this specific log as read
          setUpdateLogs((prev) => {
            const next = prev.map((log) => {
              // Match by eventId since we found it that way
              if (log.eventId === update.eventId) {
                return { ...log, isUnread: false };
              }
              return log;
            });
            console.log("[MemberDetails] markAsRead: updated logs", {
              beforeCount: prev.length,
              afterCount: next.length,
              unreadBefore: prev.filter((l) => l.isUnread).length,
              unreadAfter: next.filter((l) => l.isUnread).length
            });
            return next;
          });
          setMarkAsReadLoadingId(null);
          showNotification("Marked as read successfully!", "success");
          return; // Exit early since we handled it
        }

        // Normal flow with notificationIdToUse
        const markAsReadUrl = `${API_ENDPOINTS.ADMIN_NOTIFICATIONS}/${notificationIdToUse}/read`;
        console.log("[MemberDetails] markAsRead: calling API", {
          url: markAsReadUrl,
          notificationId: notificationIdToUse
        });

        const response = await axios.put(markAsReadUrl, null, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        });

        console.log("[MemberDetails] markAsRead: API success", {
          status: response.status,
          data: response.data
        });

        // Update local state - mark this specific log as read
        setUpdateLogs((prev) => {
          const next = prev.map((log) => {
            // Match by any of the available IDs for robustness
            if (
              log.eventId === update.eventId ||
              log.notificationId === update.notificationId ||
              (log.adminNotificationId && log.adminNotificationId === update.adminNotificationId)
            ) {
              return { ...log, isUnread: false };
            }
            return log;
          });
          console.log("[MemberDetails] markAsRead: updated logs", {
            beforeCount: prev.length,
            afterCount: next.length,
            unreadBefore: prev.filter((l) => l.isUnread).length,
            unreadAfter: next.filter((l) => l.isUnread).length
          });
          return next;
        });
        setMarkAsReadLoadingId(null);
        showNotification("Marked as read successfully!", "success");
        // Optional: Trigger a refresh of the sidebar badge count
        // You can use a custom event or context for this
        try {
          // Dispatch a custom event that the sidebar can listen to
          window.dispatchEvent(
            new CustomEvent("notificationRead", {
              detail: { notificationId: notificationIdToUse }
            })
          );
        } catch (e) {
          console.log("[MemberDetails] Could not dispatch notificationRead event", e);
        }
      } catch (error) {
        console.error("[MemberDetails] Failed to mark as read:", error);

        // More detailed error logging
        if (error.response) {
          console.error("[MemberDetails] Error response:", {
            status: error.response.status,
            data: error.response.data,
            headers: error.response.headers
          });
        } else if (error.request) {
          console.error("[MemberDetails] No response received:", error.request);
        } else {
          console.error("[MemberDetails] Error message:", error.message);
        }

        setMarkAsReadLoadingId(null);
        showNotification(error.response?.data?.message || "Failed to mark activity as read.", "error");
      }
    },
    [showNotification]
  );

  // Fetch user details (axios + list fallback from stash; Layout pattern retained)
  useEffect(() => {
    const fetchUserDetails = async () => {
      dispatch({ type: "setLoading", payload: true });
      try {
        console.log("Fetching details for membershipId:", membershipId);
        const token = await getAuthToken();
        const normalizedId = membershipId != null ? String(membershipId) : "";
        let userData = null;

        try {
          const response = await axios.get(`${API_ENDPOINTS.MEMBERS}/${membershipId}`, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const data = response.data;
          if (data && data.id) {
            userData = data;
          } else if (Array.isArray(data?.users)) {
            userData = data.users.find((user) => String(user.id) === normalizedId) || null;
          }
        } catch (singleFetchError) {
          console.warn("Single member fetch failed, will try list endpoint:", singleFetchError);
        }

        if (!userData) {
          const listResponse = await axios.get(API_ENDPOINTS.MEMBERS, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const users = listResponse.data?.users || [];
          userData = users.find((user) => String(user.id) === normalizedId) || null;
        }

        if (!userData || !userData.id) {
          showNotification("Member data not found for the given member ID.", "error");
          return;
        }

        const fetchedData = {
          fullName: userData.fullName || "",
          phoneNumber: userData.phoneNumber || "",
          email: userData.email || "",
          password: userData.password || "",
          dateOfBirth: userData.dateOfBirth || "",
          maritalStatus: userData.maritalStatus || "",
          familyMembers: userData.familyMembers || "",
          roleType: userData.roleType || "",
          baptismStatus: userData.baptismStatus || "",
          address: userData.address || "",
          testimony: userData.testimony || "",
          additionalField1: userData.additionalField1 || "",
          additionalField2: userData.additionalField2 || "",
          areasOfInterest: userData.areasOfInterest || "",
          baptismCertificateUrl: userData.baptismCertificateUrl || "",
          countryCode: userData.countryCode || "",
          customInterest: userData.customInterest || "",
          dateOfBaptism: userData.dateOfBaptism || "",
          documentURL: userData.documentURL || "",
          familyMemberNumber: userData.familyMemberNumber || "",
          familyMemberNames: userData.familyMemberNames || "",
          fileName: userData.fileName || "",
          marriageCertificateUrl: userData.marriageCertificateUrl || "",
          mimeType: userData.mimeType || "",
          residenceProofUrl: userData.residenceProofUrl || "",
          termsAccepted: userData.termsAccepted || false,
          profileImage: userData.profileImage || null,
          residenceProof: userData.residenceProof || "",
          marriageCertificate: userData.marriageCertificate || "",
          baptismCertificate: userData.baptismCertificate || "",
          userId: userData.userId || "",
          status: userData.status || ""
        };

        dispatch({ type: "setFormData", payload: fetchedData });
        form.setFieldsValue({
          ...fetchedData,
          dateOfBirth: fetchedData.dateOfBirth ? dayjs(fetchedData.dateOfBirth) : null,
          dateOfBaptism: fetchedData.dateOfBaptism ? dayjs(fetchedData.dateOfBaptism) : null
        });

        setLoadingLogs(true);
        setLogsError(null);
        try {
          const logsRes = await fetch(API_ENDPOINTS.MEMBER_UPDATES, {
            headers: { Authorization: `Bearer ${token}` }
          });
          const logsData = await logsRes.json();
          console.log("Activity logs response:", logsData);

          if (logsRes.ok) {
            // Filter logs for this specific user
            const memberLogs = (logsData.items || []).filter((log) => log.userId === fetchedData.userId);

            // Log the IDs for debugging
            memberLogs.forEach((log) => {
              console.log(`Log: eventId=${log.eventId}, notificationId=${log.notificationId}, adminNotificationId=${log.adminNotificationId}`);
            });

            setUpdateLogs(memberLogs);
          } else {
            setLogsError("Failed to fetch update logs");
          }
        } catch (err) {
          console.error("Error fetching update logs:", err);
          setLogsError("Error fetching update logs");
        } finally {
          setLoadingLogs(false);
        }
      } catch (error) {
        console.error("Error fetching user details:", error);
        showNotification(error.response?.data?.message || "An error occurred while fetching user details.", "error");
      } finally {
        dispatch({ type: "setLoading", payload: false });
      }
    };
    fetchUserDetails();
  }, [membershipId, form, showNotification]);

  const modalTitle = useMemo(() => `Send Message to ${formData.fullName}`, [formData.fullName]);

  const unreadCount = useMemo(() => updateLogs.filter((log) => log.isUnread).length, [updateLogs]);

  // Memoize document items for rendering
  const documentItems = useMemo(
    () => [
      {
        label: "Residence Proof",
        url: formData.residenceProofUrl,
        key: "residenceProof"
      },
      {
        label: "Baptism Certificate",
        url: formData.baptismCertificateUrl,
        key: "baptismCertificate"
      },
      {
        label: "Marriage Certificate",
        url: formData.marriageCertificateUrl,
        key: "marriageCertificate"
      }
    ],
    [formData.residenceProofUrl, formData.baptismCertificateUrl, formData.marriageCertificateUrl]
  );

  return (
    <Layout
      navTitle="Member Management"
      notificationProps={{
        show: notification.show,
        message: notification.message,
        type: notification.type,
        onClose: hideNotification,
        duration: 4000
      }}
    >
      <div
        style={{
          display: "flex",
          gap: 24,
          width: "100%",
          alignItems: "flex-start",
          padding: 20
        }}
      >
        <div className="mbd-form-container">
          <div className="mbd-form-wrapper">
            {isLoading ? (
              <div style={{ textAlign: "center", padding: "32px" }}>
                <Spin size="large" style={{ marginBottom: "10px" }} />
                <p>Loading member details...</p>
              </div>
            ) : (
              <Form form={form} layout="vertical" className="mbd-antd-form">
                <div className="mbd-form-section">
                  <h3 className="mbd-section-title">Personal Information</h3>
                  <div className="mbd-form-row">
                    <Form.Item label="Full Name" name="fullName">
                      <Input disabled />
                    </Form.Item>
                    <Form.Item label="Date of Birth" name="dateOfBirth">
                      <DatePicker disabled style={{ width: "100%" }} format="YYYY-MM-DD" />
                    </Form.Item>
                    <Form.Item label="Role Type" name="roleType">
                      <Input disabled />
                    </Form.Item>
                    <Form.Item label="Membership Status" name="status">
                      <Input disabled />
                    </Form.Item>
                  </div>
                </div>

                <div className="mbd-form-section">
                  <h3 className="mbd-section-title">Contact Information</h3>
                  <div className="mbd-form-row">
                    <Form.Item label="Phone Number" name="phoneNumber">
                      <Input disabled />
                    </Form.Item>
                    <Form.Item label="Email" name="email">
                      <Input disabled />
                    </Form.Item>
                  </div>
                  <Form.Item label="Address" name="address">
                    <TextArea rows={3} disabled />
                  </Form.Item>
                </div>

                <div className="mbd-form-section">
                  <h3 className="mbd-section-title">Baptism and Marital Information</h3>
                  <div className="mbd-form-row">
                    <Form.Item label="Baptism Status" name="baptismStatus">
                      <Input disabled />
                    </Form.Item>
                    <Form.Item label="Date of Baptism" name="dateOfBaptism">
                      <DatePicker disabled style={{ width: "100%" }} format="YYYY-MM-DD" />
                    </Form.Item>
                    <Form.Item label="Marital Status" name="maritalStatus">
                      <Input disabled />
                    </Form.Item>
                  </div>
                  <Form.Item label="Family Members" name="familyMembers">
                    <TextArea rows={3} disabled />
                  </Form.Item>
                </div>

                <div className="mbd-form-section">
                  <h3 className="mbd-section-title">Additional Information</h3>
                  <Form.Item label="Areas of Interest" name="areasOfInterest">
                    <TextArea rows={3} disabled />
                  </Form.Item>
                  <Form.Item label="Testimony" name="testimony">
                    <TextArea rows={4} disabled />
                  </Form.Item>
                  <Form.Item label="Custom Interest" name="customInterest">
                    <Input disabled />
                  </Form.Item>
                </div>

                <div className="mbd-form-section">
                  <h3 className="mbd-section-title">Document Uploads</h3>
                  <div className="mbd-files-section">
                    <div className="mbd-file-list">
                      {documentItems.map((doc) => (
                        <div key={doc.key} className="mbd-file-item">
                          <label className="mbd-file-label">{doc.label}:</label>
                          {doc.url ? (
                            <div className="mbd-file-content">
                              <span className="mbd-file-name">📄 {doc.url.split("/").pop()}</span>
                              <Button className="mbd-file-item-btn" onClick={() => viewSupportingDoc(doc.url, doc.key)} loading={loadingFiles[doc.key]}>
                                {loadingFiles[doc.key] ? "Loading..." : "View File"}
                              </Button>
                            </div>
                          ) : (
                            <span className="mbd-no-document">No document uploaded.</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="mbd-form-section">
                  <h3 className="mbd-section-title">Terms and Conditions</h3>
                  <Form.Item name="termsAccepted" valuePropName="checked">
                    <Checkbox disabled>Terms Accepted</Checkbox>
                  </Form.Item>
                </div>

                <div className="mbd-form-btn-group">
                  <Button className="mbd-form-cancel-btn" onClick={handleCancel}>
                    Cancel
                  </Button>
                  <Button className="mbd-form-message-btn" onClick={handleOpenModal}>
                    Send Message
                  </Button>
                </div>
              </Form>
            )}
          </div>
        </div>

        <div className="mbd-activity-history">
          <MemberUpdateHistory
            updates={updateLogs}
            loading={loadingLogs}
            error={logsError}
            unreadCount={unreadCount}
            onMarkAsRead={handleMarkAsRead}
            markAsReadLoadingId={markAsReadLoadingId}
          />
        </div>
      </div>

      <Modal
        className="mbd-message-modal"
        title={modalTitle}
        open={showModal}
        onOk={handleSendMessage}
        onCancel={handleModalCancel}
        okText="Send Message"
        cancelText="Cancel"
        width={600}
        confirmLoading={isSendingMessage}
      >
        <TextArea rows={6} placeholder="Type your message here..." value={messageBody} onChange={handleMessageBodyChange} />
      </Modal>
    </Layout>
  );
}

export default MemberDetails;
